import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@workos-inc/authkit-react";
import { MainComponent } from "./MainComponent";
import { LayoutProvider } from "../../context/LayoutContext";
import { useJobStore } from "../../store/jobStore";
import { queryClient } from "../../shared/api/queryClient";
import { Box, CircularProgress } from "@mui/material";

export function ProtectedView({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const clearStore = useJobStore((state) => state.clearStore);
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (isLoading) return;

    if (!user && !hasRedirected.current) {
      hasRedirected.current = true;
      console.warn("[ProtectedView] Not authenticated, redirecting to login");
      clearStore();
      // Session expiry is a client-side navigate (no page reload), so the
      // officer's profile/templates/roster would otherwise sit in the
      // module query cache on the login screen — clear it with auth.
      queryClient.clear();
      navigate("/login", { replace: true });
    }
  }, [isLoading, user, clearStore, navigate]);

  // Reset the redirect guard if user becomes authenticated
  useEffect(() => {
    if (user) {
      hasRedirected.current = false;
    }
  }, [user]);

  if (isLoading || !user) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LayoutProvider>
      <MainComponent>{children}</MainComponent>
    </LayoutProvider>
  );
}
